/**
 * useServedByInfo — one shared in-memory cache for the "served-by"
 * microline (nearest airport, drive time, cars/transfers available)
 * exposed by `/api/destinations/served-by/{iata}`.
 *
 * Contract:
 *   - Fetch once per IATA per session; return cached copy on subsequent
 *     hooks. Zero re-fetches, zero waterfalls.
 *   - On failure, return null (component renders nothing — safe fallback).
 *   - No hallucinated data: `hasCars` / `hasTransfer` are only ever true
 *     when the registry says so.
 *
 * Phase-2 Trust Engine — the FlightCard consumes this to answer
 *   "Is this airport legit and can I get from there to my hotel?"
 * before the affiliate click.
 */
import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const _CACHE = new Map();          // iata -> Promise | resolved data
const _PENDING = new Map();

export default function useServedByInfo(iata) {
  const code = (iata || '').trim().toUpperCase();
  const [data, setData] = useState(() => (_CACHE.has(code) ? _CACHE.get(code) : null));

  useEffect(() => {
    if (!code) { setData(null); return undefined; }
    if (_CACHE.has(code)) {
      setData(_CACHE.get(code));
      return undefined;
    }
    let cancelled = false;
    const existing = _PENDING.get(code);
    const p = existing || fetch(`${API_BASE}/api/destinations/served-by/${code}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        _CACHE.set(code, json);
        _PENDING.delete(code);
        return json;
      })
      .catch(() => {
        _CACHE.set(code, null);   // memoise the miss too
        _PENDING.delete(code);
        return null;
      });
    if (!existing) _PENDING.set(code, p);
    p.then((json) => { if (!cancelled) setData(json); });
    return () => { cancelled = true; };
  }, [code]);

  return data;
}
